import { useRowClick } from '@/lib/navigation';
import { type SortDirection, type SortValue, type TableSort, nextSort, sortRows } from '@/lib/tableSort';
import { cn } from '@/lib/utils';
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    useIsStickyHeaderClone,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

/**
 * One column. Sorting comes free with the column: name a stored field in `sortField` (the query orders
 * the rows) or an accessor in `sortBy` (this table orders them), and the header, the indicator, the
 * URL round trip and the tap cycle are all wired up. Declare neither and the column doesn't sort —
 * which is the honest answer for a derived cell (an array length, a joined display name) that no
 * stored field backs.
 */
export type DataTableColumn<T> = {
    key: string;
    /** Omit for an unlabeled column, e.g. a trailing row-actions cell. */
    header?: ReactNode;
    cell: (row: T) => ReactNode;
    /** Applied to both the header cell and every body cell — widths belong here. */
    className?: string;
    /** Body cells only. */
    cellClassName?: string;
    /** Stored Firestore field to order by. Server-paginated tables use this. */
    sortField?: string;
    /** Client-side value accessor. Fully-loaded tables use this. */
    sortBy?: (row: T) => SortValue;
    /** Direction the first tap applies. Dates want 'desc' — "newest first" is what sorting one means. */
    sortFirst?: SortDirection;
    /** Caveat shown under the table while this sort is active, e.g. which rows the ordering hides. */
    sortNote?: string;
};

/**
 * Row selection: a checkbox per row, plus one in the header for every selectable row. While it is on, a
 * row click toggles the row instead of following `rowHref`.
 */
export type DataTableSelection<T> = {
    /** Selected row keys, as `rowKey` gives them. */
    selected: ReadonlySet<string>;
    onChange: (selected: Set<string>) => void;
    /** Rows that can't be picked show a disabled checkbox. Every row is selectable when omitted. */
    isSelectable?: (row: T) => boolean;
    /** The selectable rows the header checkbox picks, e.g. only those not yet done. Every selectable row when omitted. */
    inSelectAll?: (row: T) => boolean;
};

export type DataTableProps<T> = {
    columns: DataTableColumn<T>[];
    rows: T[];
    rowKey: (row: T) => string;
    /** Makes rows clickable, with cmd+click opening a new tab (see `useRowClick`). */
    rowHref?: (row: T) => string;
    rowClassName?: string;
    sort?: TableSort;
    onSortChange?: (sort: TableSort | undefined) => void;
    /**
     * Why a column can't be sorted right now, e.g. a prefix search that pins the query's ordering.
     * Returning a string disables that header and shows the reason on hover.
     */
    sortDisabledReason?: (column: DataTableColumn<T>) => string | undefined;
    selection?: DataTableSelection<T>;
};

export function DataTable<T>({
    columns,
    rows,
    rowKey,
    rowHref,
    rowClassName,
    sort,
    onSortChange,
    sortDisabledReason,
    selection,
}: DataTableProps<T>) {
    const onRowClick = useRowClick();
    // Asked only about columns that can sort at all: a reason like "unavailable while searching" is
    // false for a column that never sorted, and greying every header reads as a disabled table.
    const disabledReason = (column: DataTableColumn<T>) =>
        isSortable(column, onSortChange) ? sortDisabledReason?.(column) : undefined;
    const sortedColumn = columns.find((column) => column.key === sort?.key);
    // A disabled column isn't sorting anything, whatever the URL still says — so it gets no indicator,
    // no caption and no client-side pass.
    const activeColumn = sortedColumn && !disabledReason(sortedColumn) ? sortedColumn : undefined;
    const activeSort = activeColumn ? sort : undefined;

    // `sortField` columns are already ordered by the query; only `sortBy` columns are ours to sort.
    const sortedRows = useMemo(() => {
        if (!activeSort || !activeColumn?.sortBy) return rows;
        return sortRows(rows, activeColumn.sortBy, activeSort.dir);
    }, [rows, activeSort, activeColumn]);

    const selectableKeys = selection ? rows.filter((row) => selection.isSelectable?.(row) ?? true).map(rowKey) : [];
    const selectAllKeys = selection
        ? rows
              .filter((row) => (selection.isSelectable?.(row) ?? true) && (selection.inSelectAll?.(row) ?? true))
              .map(rowKey)
        : [];
    const selectedCount = selectableKeys.filter((key) => selection?.selected.has(key)).length;
    const allPicked = selectAllKeys.length > 0 && selectAllKeys.every((key) => selection?.selected.has(key));
    const toggleRow = (key: string) => {
        if (!selection) return;
        const next = new Set(selection.selected);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        selection.onChange(next);
    };
    const rowClickHandler = (row: T) => {
        if (selection) {
            const key = rowKey(row);
            return selectableKeys.includes(key) ? () => toggleRow(key) : undefined;
        }
        return rowHref ? onRowClick(rowHref(row)) : undefined;
    };

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    {selection && (
                        <SelectAllHead
                            checked={selectedCount === 0 ? false : allPicked ? true : 'indeterminate'}
                            disabled={selectAllKeys.length === 0 && selectedCount === 0}
                            onCheckedChange={(checked) =>
                                selection.onChange(new Set(checked === true ? selectAllKeys : []))
                            }
                        />
                    )}
                    {columns.map((column) => (
                        <SortableHead
                            key={column.key}
                            column={column}
                            sort={sort}
                            onSortChange={onSortChange}
                            disabledReason={disabledReason(column)}
                        />
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {sortedRows.map((row) => (
                    <TableRow
                        key={rowKey(row)}
                        className={cn(
                            rowClickHandler(row) && 'cursor-pointer transition-colors hover:bg-muted/50',
                            rowClassName,
                        )}
                        onClick={rowClickHandler(row)}
                    >
                        {selection && (
                            <TableCell className="w-10">
                                <Checkbox
                                    aria-label="Select row"
                                    checked={selection.selected.has(rowKey(row))}
                                    disabled={!selectableKeys.includes(rowKey(row))}
                                    onClick={(e) => e.stopPropagation()}
                                    onCheckedChange={() => toggleRow(rowKey(row))}
                                />
                            </TableCell>
                        )}
                        {columns.map((column) => (
                            <TableCell key={column.key} className={cn(column.className, column.cellClassName)}>
                                {column.cell(row)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
            {activeSort && activeColumn?.sortNote && <TableCaption>{activeColumn.sortNote}</TableCaption>}
        </Table>
    );
}

/** The select-all box. Out of the tab order in the pinned header's clone, as `SortableHead`'s button is. */
function SelectAllHead({
    checked,
    disabled,
    onCheckedChange,
}: {
    checked: boolean | 'indeterminate';
    disabled: boolean;
    onCheckedChange: (checked: boolean | 'indeterminate') => void;
}) {
    const isClone = useIsStickyHeaderClone();
    return (
        <TableHead className="w-10">
            <Checkbox
                aria-label="Select all"
                tabIndex={isClone ? -1 : undefined}
                onMouseDown={isClone ? (e) => e.preventDefault() : undefined}
                checked={checked}
                disabled={disabled}
                onCheckedChange={onCheckedChange}
            />
        </TableHead>
    );
}

function SortableHead<T>({
    column,
    sort,
    onSortChange,
    disabledReason,
}: {
    column: DataTableColumn<T>;
    sort: TableSort | undefined;
    onSortChange: ((sort: TableSort | undefined) => void) | undefined;
    disabledReason: string | undefined;
}) {
    // Duplicated in the pinned header clone, where a second tab stop inside `aria-hidden` would be one
    // tab stop too many — the real header keeps the keyboard path.
    const isClone = useIsStickyHeaderClone();
    const sortable = isSortable(column, onSortChange);
    const active = sort?.key === column.key ? sort.dir : undefined;

    if (!sortable || !onSortChange || disabledReason) {
        return (
            <TableHead className={column.className} title={disabledReason}>
                <span className={cn(disabledReason && 'text-muted-foreground')}>{column.header}</span>
            </TableHead>
        );
    }

    const Indicator = active === 'asc' ? ArrowUp : active === 'desc' ? ArrowDown : ChevronsUpDown;

    return (
        <TableHead className={column.className} aria-sort={active ? ARIA_SORT[active] : 'none'}>
            <button
                type="button"
                tabIndex={isClone ? -1 : undefined}
                // Clicking the clone would otherwise park focus inside an `aria-hidden` subtree; the
                // real header keeps it.
                onMouseDown={isClone ? (e) => e.preventDefault() : undefined}
                onClick={() => onSortChange(nextSort(sort, column.key, column.sortFirst))}
                className="-mx-1 flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
                {column.header}
                <Indicator className={cn('h-3.5 w-3.5 shrink-0', !active && 'text-muted-foreground/50')} />
            </button>
        </TableHead>
    );
}

function isSortable<T>(column: DataTableColumn<T>, onSortChange: DataTableProps<T>['onSortChange']): boolean {
    return !!onSortChange && (!!column.sortField || !!column.sortBy);
}
